import { ref, computed, Ref } from 'vue';
import { defineStore } from 'pinia';
import { isEmpty, peek, pop } from 'src/common/arrayUtil';

// eslint-disable-next-line
type Identifiable = string | number | any;

export interface Equals<T extends Identifiable> {
  (one: T, two: T): boolean;
}

function createHistoryStore<T extends Identifiable>(equals: Equals<T>) {

  return () => {

    const prevStack = ref<T[]>([]) as Ref<T[]>;
    const nextStack = ref<T[]>([]) as Ref<T[]>;
    const currentData = ref<T>();

    const peakPrev = computed(() => peek(prevStack.value));
    const peakNext = computed(() => peek(nextStack.value));
    const isCurrentAvailable = computed(() => currentData.value !== undefined);
    const isPrevAvailable = computed(() => !isEmpty(prevStack.value));
    const isNextAvailable = computed(() => !isEmpty(nextStack.value));

    function clearHistory() {
      prevStack.value.length = 0;
      nextStack.value.length = 0;
    }

    function setCurrentData(value?: T) {

      currentData.value = value;
    }
    function addToHistory(data: T) : T {

      const peekedValue = peek(prevStack.value) as T;
      if (currentData.value !== undefined && (peekedValue === undefined || !equals(peekedValue, currentData.value))) {
        prevStack.value.push(currentData.value);
      }
      setCurrentData(data);
      nextStack.value = [];
      return data;
    }

    function getNext() : T | undefined {

      if (isEmpty(nextStack.value)) {
        return undefined;
      } else {
        if (currentData.value !== undefined) {
          prevStack.value.push(currentData.value)
        }
        setCurrentData(pop(nextStack.value));
        return currentData.value;
      }
    }

    function getPrev() : T | undefined {

      if (isEmpty(prevStack.value)) {
        return undefined;
      } else {
        if (currentData.value !== undefined) {
          nextStack.value.push(currentData.value)
        }
        const peekedValue = peek(prevStack.value) as T;
        if (currentData.value !== undefined && equals(peekedValue, currentData.value)) {
          pop(prevStack.value);
        }
        setCurrentData(pop(prevStack.value));
        return currentData.value;
      }
    }

    function reinitialize(data?: T) {
      prevStack.value = [];
      nextStack.value = [];
      setCurrentData(data);
    }

    return {
      peakPrev,
      peakNext,
      isCurrentAvailable,
      isPrevAvailable,
      isNextAvailable,
      addToHistory,
      getNext,
      getPrev,
      reinitialize,
      currentData,
      clearHistory
    }
  }
}

export function useHistoryStore<T extends Identifiable>(name: string, equals?: Equals<T>) {

  if (!equals) {
    equals = (one?: T, two?: T) => one === two;
  }

  return (defineStore(name, createHistoryStore<T>(equals)))();
}
